import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import DeviceInitializationSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/DeviceInitializationSimulator';
import {
  simulateAllianceSerialDeviceInitialization,
  simulateDexterDeviceInitialization,
  simulateMachineDrivenDeviceTransaction,
  simulateDeviceStatusEvent,
  simulateTechnicalServiceDeviceTransaction,
  simulateCustomerServiceDeviceTransaction,
  simulateLiveLinkDeviceTransaction,
  simulateFullServiceDeviceTransaction,
  simulateElectroluxSerialDeviceInitialization,
} from 'api/admin/devices/devices';
import toaster from 'react-hot-toast';
import styles from 'components/admin/deviceDashboard/deviceEditor/DeviceSimulator.module.scss';
import { MACHINE_MANUFACTURERS } from 'utils/constants';
import DeviceTransactionSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/DeviceTransactionSimulator';
import DeviceStatusSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/status/DeviceStatusSimulator';

/**
 * The parent component that contains the logic for displaying the various wizards required
 * for simulating device activity.
 *
 * This component specifically lets you choose the simulation type, and then displays the
 * components containing the various wizards for each simulation type.
 *
 * Additionally, the APIs for simulating device events will be called here
 *
 * @param {Object} props
 */
const DeviceSimulator = (props) => {
  const queryClient = useQueryClient();
  const { device, onBack } = props;
  const fullSimulationTypes = [
    {
      display: 'Initialization',
      value: 'DEVICE_INITIALIZATION',
    },
    {
      display: 'Run Machine',
      value: 'RUN_MACHINE',
    },
    {
      display: 'Status Change',
      value: 'STATUS_CHANGE',
    },
  ];

  const [simulationOptions, setSimulationOptions] = useState(fullSimulationTypes);
  const [simulationType, setSimulationType] = useState(null);
  const [simulationScreenType, setSimulationScreenType] = useState(null);
  const [deviceTransactionType, setDeviceTransactionType] = useState(null);

  /**
   * Determine the proper simulation options to display based on device data
   */
  useEffect(() => {
    if (device?.isPaired) {
      setSimulationOptions([
        {
          display: 'Run Machine',
          value: 'RUN_MACHINE',
        },
        {
          display: 'Status Change',
          value: 'STATUS_CHANGE',
        },
      ]);
    }
  }, []);

  /**
   * Set the selected simulation type in state
   *
   * @param {object} event
   * @param {object} value
   */
  const handleDropdownFieldChange = (event = null, value) => {
    setSimulationType(value.value);
  };

  /**
   * Handle back functionality for the simulation wizard
   */
  const handleBackFunctionality = () => {
    setSimulationType(null);
    setSimulationScreenType(null);
  };

  /**
   * call the relevant API for simulating device transactions
   */
  const { isLoading: isDeviceTransactionLoading, mutate: simulateDeviceTransaction } = useMutation({
    mutationFn: (data) => {
      switch (deviceTransactionType) {
        case 'SELF_SERVICE':
          // this is a shortcut way to determine if this is a machine-driven or live-link driven simulation
          if (data.paymentStatus) {
            return simulateMachineDrivenDeviceTransaction(data);
          }
          return simulateLiveLinkDeviceTransaction(data);
        case 'TECHNICAL_SERVICE':
          return simulateTechnicalServiceDeviceTransaction(data);
        case 'CUSTOMER_SERVICE': 
          return simulateCustomerServiceDeviceTransaction(data);
        case 'FULL_SERVICE':
          return simulateFullServiceDeviceTransaction(data);
        default: 
          break;
      }
    },
    onSuccess: () => {
      toaster('The machine has been successfully run!', {
        icon: '✅',
      });
      queryClient.invalidateQueries({ queryKey: ['fetchIndividualDevice', { id: device.id }] });
      onBack();
    },
    onError: (error) => {
      toaster(`Error in simulating device transaction: ${error?.response?.data?.error}`);
    },
  });

  /**
   * call the relevant API for simulating device initialization
   */
  const { isLoading: isSimulationLoading, mutate: simulateDeviceInitializationMutation } =
    useMutation({
      mutationFn: (data) => {
        if (data.manufacturer === MACHINE_MANUFACTURERS.DEXTER) {
          return simulateDexterDeviceInitialization(data);
        }

        if (data.manufacturer === MACHINE_MANUFACTURERS.ELECTROLUX) {
          return simulateElectroluxSerialDeviceInitialization(data);
        }

        return simulateAllianceSerialDeviceInitialization(data);
      },
      onSuccess: () => {
        toaster('Device has been successfully initialized and paired!', {
          icon: '✅',
        });
        queryClient.invalidateQueries({ queryKey: ['fetchIndividualDevice', { id: device.id }] });
        onBack();
      },
      onError: (error) => {
        toaster(`Error in initializing device: ${error?.response?.data?.error}`);
      },
    });

  /**
   * call the relevant API for simulating device status events
   */
  const { isLoading: isDeviceStatusSimulationLoading, mutate: simulateDeviceStatusMutation } =
    useMutation({
      mutationFn: (data) => {
        return simulateDeviceStatusEvent(data);
      },
      onSuccess: () => {
        toaster(`Machine and device status has been simulated!`, {
          icon: '✅',
        });
        queryClient.invalidateQueries({ queryKey: ['fetchIndividualDevice', { id: device.id }] });
        onBack();
      },
      onError: (error) => {
        toaster(`Error in simulation device status: ${error?.response?.data?.error}`);
      },
    });

  /**
   * submit the data to simulate machine / device initialization
   *
   * @param {object} data
   */
  const handleDeviceSimulationSubmitEvent = (data) => {
    simulateDeviceInitializationMutation(data);
  };

  /**
   * submit the data to simulate device transactions
   *
   * @param {object} data
   */
  const handleDeviceTransactionSimulationSubmitEvent = (data) => {
    simulateDeviceTransaction(data);
  };

  /**
   * submit the data to simulate device transactions
   *
   * @param {object} data
   */
  const handleDeviceStatusSimulationSubmitEvent = (data) => {
    simulateDeviceStatusMutation(data);
  };

  /**
   * render the initialization simulation wizard
   */
  const renderInitializationWizard = () => {
    return (
      <DeviceInitializationSimulator
        device={device}
        onBack={handleBackFunctionality}
        onSubmit={handleDeviceSimulationSubmitEvent}
      />
    );
  };

  /**
   * render the transaction simulation wizard
   */
  const renderTransactionWizard = () => {
    return (
      <DeviceTransactionSimulator
        device={device}
        onBack={handleBackFunctionality}
        onSubmit={handleDeviceTransactionSimulationSubmitEvent}
        setDeviceTransactionType={setDeviceTransactionType}
      />
    );
  };

  /**
   * render the device status simulation wizard
   */
  const renderDeviceStatusWizard = () => {
    return (
      <DeviceStatusSimulator
        device={device}
        onBack={handleBackFunctionality}
        onSubmit={handleDeviceStatusSimulationSubmitEvent}
      />
    );
  }

  /**
   * hamdle the logic for clicking next after determining the simulation event
   */
  const handleNextButtonLogic = () => {
    setSimulationScreenType(simulationType);
  };

  /**
   * display the initial selection option for choosing the simulation event type
   */
  const showInitializationSelection = () => {
    return (
      <>
        <header className={styles.header}>
          <h4>Welcome to the Cents Device Event Simulator!</h4>
          <p>
            Here, you will be able to test out any device events, like pairing a device, running a
            machine, or receiving status updates, without actually needing a real machine or a real
            device connected to anything.
          </p>
        </header>
        <main className={styles.main}>
          <section className={styles.section}>
            <p>Choose the simulation event</p>
            <Autocomplete
              disablePortal
              id="autocomplete-choose-simulation-event"
              getOptionLabel={(option) => option.display}
              options={simulationOptions}
              sx={{ width: '300px' }}
              onChange={handleDropdownFieldChange}
              renderInput={(params) => <TextField {...params} label="Simulation Event" />}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
          </section>
        </main>
        <footer className={styles.footer}>
          <p className={styles.backButton} onClick={onBack}>
            Back
          </p>
          <button
            className={classNames(styles.backButton, {
              disabled: !simulationType,
            })}
            disabled={!simulationType}
            onClick={handleNextButtonLogic}
          >
            Next
          </button>
        </footer>
      </>
    );
  };

  /**
   * determine whether to show the initial simulation selection or the individual wizards
   */
  const renderSimulatorLogic = () => {
    switch (simulationScreenType) {
      case 'DEVICE_INITIALIZATION':
        return renderInitializationWizard();

      case 'RUN_MACHINE':
        return renderTransactionWizard();

      case 'STATUS_CHANGE':
        return renderDeviceStatusWizard();

      default:
        return showInitializationSelection();
    }
  };

  return (
    <div id={styles.deviceSimulatorContainer}>
      {(isSimulationLoading || isDeviceTransactionLoading || isDeviceStatusSimulationLoading) ? <CentsLoader /> : renderSimulatorLogic()}
    </div>
  );
};

export default DeviceSimulator;
